// Recruiting — the candidate card's write surface. Mounted where
// CandidateCard.tsx currently renders its disabled "Next build" chip row
// (client/src/components/recruiting/CandidateCard.tsx) — see the header
// comment there. This file owns every ACTION on a candidate; the board, the
// table and the card's read-only body belong to a different pass and are
// never touched from here (lib/recruitingApi.ts / lib/store.ts do the reads).
//
// ONE FORM OPEN AT A TIME: clicking a chip opens its form beneath the row and
// closes whichever other one was open — the same discipline as
// ManagerEntry.tsx's one-line-at-a-time editor. Cancel, or a successful save,
// closes it and (on save) calls `onChanged` so the caller can refetch the
// candidate and the board list.
//
// EVERY WRITE GOES THROUGH lib/recruitingWrite.ts, which rides authedWrite
// (lib/apiClient.ts) — bearer token plus a fresh Idempotency-Key per call, so
// a retried Save is automatically a new attempt, never a replay of a stale
// key. Busy/error handling is the app's own `useWrite` (MoneyForms.tsx),
// shared by every other write control in this app rather than reinvented.
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { LOCATION_ORDER, LOCATION_NAMES } from '../../../lib/locations';
import { useResource } from '../../../lib/store';
import {
  RECRUITING_STAGES, STAGE_LABEL, RECRUITING_TEMPLATES_KEY, fetchRecruitingTemplates,
  fetchRecruitingCandidates, type CandidateRow,
} from '../../../lib/recruitingApi';
import { ApiError } from '../../../lib/apiClient';
import { useWrite } from '../../MoneyForms';
import {
  type Candidate, type LogKind,
  addCandidateLog, moveCandidateStage, setCandidateOutcome, setDoNotContact,
  updateCandidate, attachCandidateFile, existingCandidateConflict,
  sendCandidateEmail, fixRecruitingWording, sendToManager,
  mergeCandidate, setCandidateReferral, markLicenseChecked, type LicenseCheckedStatus,
} from '../../../lib/recruitingWrite';
import {
  type RecruitingViewer, POSITIONS, POSITION_LABEL, OUTCOMES, OUTCOME_LABEL,
} from './constants';

export type { RecruitingViewer } from './constants';

const SEARCH_DEBOUNCE_MS = 300;

/** The same shared roster cache PeopleManagement.tsx's own `PersonSelect`
 * reads (client/src/lib/bootstrap.ts populates it once, at sign-in) — never
 * a second fetch of the roster just for this one picker. */
interface RosterPerson {
  id: number;
  full_name: string;
  status: string;
}

/** Where the Manager Entry form actually lives. There is no dedicated
 * `/manager-entry` route (checked against src/app.tsx) — Manager Entry is a
 * sub-tab of the Payroll page (src/pages/Payroll.tsx: managers get it
 * directly, owner/director pick the "Manager entry" sub-tab). Landing here
 * with `?candidate=` is only useful once that page is taught to read the
 * query and, for a non-manager viewer, switch itself to that sub-tab — both
 * outside this file's scope. Flagged in the handoff report. */
function managerEntryUrl(candidateId: number): string {
  return `/payroll?candidate=${candidateId}`;
}

/** Shared "What happened" box for Note / Call / Text — same endpoint
 * (POST .../log), only `kind` differs. */
function LogForm(
  { kind, candidateId, onDone, onCancel }:
  { kind: Extract<LogKind, 'NOTE' | 'CALL' | 'TEXT'>; candidateId: number; onDone: () => void; onCancel: () => void },
): JSX.Element {
  const [body, setBody] = useState('');
  const write = useWrite();
  const canSave = body.trim() !== '';
  const testKind = kind.toLowerCase();

  function save(): void {
    if (!canSave) return;
    void write.run(async () => {
      await addCandidateLog(candidateId, kind, body.trim());
    }, onDone);
  }

  return (
    <div data-testid={`log-form-${testKind}`}>
      <label class="nh-field">
        <span class="nh-label">What happened</span>
        <textarea
          class="notes-entry" rows={8} aria-label="What happened"
          value={body} disabled={write.busy}
          onInput={(e: Event) => setBody((e.target as HTMLTextAreaElement).value)}
        />
      </label>
      <div class="money-add">
        <button
          type="button" class="btn primary" disabled={write.busy || !canSave}
          onClick={save} data-testid={`log-save-${testKind}`}
        >
          {write.busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" class="btn" onClick={onCancel} data-testid={`log-cancel-${testKind}`}>Cancel</button>
      </div>
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </div>
  );
}

function DoNotContactForm(
  { candidate, onDone, onCancel }:
  { candidate: Candidate; onDone: () => void; onCancel: () => void },
): JSX.Element {
  const [reason, setReason] = useState('');
  const write = useWrite();
  const turningOn = !candidate.doNotContact;
  const label = turningOn ? 'Mark do not contact' : 'Lift do not contact';
  const canSave = reason.trim() !== '';

  function save(): void {
    if (!canSave) return;
    void write.run(async () => {
      await setDoNotContact(candidate.id, turningOn, reason.trim());
    }, onDone);
  }

  return (
    <div class="money-add" data-testid="dnc-form">
      <label class="nh-field">
        <span class="nh-label">Reason</span>
        <input
          type="text" aria-label="Reason" value={reason} disabled={write.busy}
          onInput={(e: Event) => setReason((e.target as HTMLInputElement).value)}
        />
      </label>
      <button type="button" class="btn primary" disabled={write.busy || !canSave} onClick={save} data-testid="dnc-save">
        {write.busy ? 'Saving…' : label}
      </button>
      <button type="button" class="btn" onClick={onCancel} data-testid="dnc-cancel">Cancel</button>
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </div>
  );
}

/** First/last/email/phone/license/position/clinic/hiring manager/address/
 * city/state/zip. A value already on the candidate that falls OUTSIDE the
 * option list this form offers (a migrated position, or — for a MANAGER,
 * whose clinic select only ever lists their own clinic — a candidate who
 * belongs to a different one) is shown as its own disabled option rather than
 * silently falling back to the first one in the list. Without that, opening
 * and saving Edit would quietly overwrite the true value with whatever the
 * select defaulted to — same discipline as Tasks.tsx's status <select> for a
 * status its menu may not set. */
function EditForm(
  { candidate, viewer, onDone, onCancel }:
  { candidate: Candidate; viewer: RecruitingViewer; onDone: () => void; onCancel: () => void },
): JSX.Element {
  const [firstName, setFirstName] = useState(candidate.firstName);
  const [lastName, setLastName] = useState(candidate.lastName);
  const [email, setEmail] = useState(candidate.email ?? '');
  const [phone, setPhone] = useState(candidate.phone ?? '');
  const [licenseNumber, setLicenseNumber] = useState(candidate.licenseNumber ?? '');
  const [position, setPosition] = useState(candidate.position ?? '');
  const [homeLocationCode, setHomeLocationCode] = useState(candidate.homeLocationCode ?? '');
  const [hiringManager, setHiringManager] = useState(candidate.hiringManager ?? '');
  const [address, setAddress] = useState(candidate.address ?? '');
  const [city, setCity] = useState(candidate.city ?? '');
  const [stateAbbr, setStateAbbr] = useState(candidate.state ?? '');
  const [zip, setZip] = useState(candidate.zip ?? '');
  const [conflictId, setConflictId] = useState<number | null>(null);
  const write = useWrite();

  const clinicOptions = viewer.role === 'MANAGER' && viewer.locationCode ? [viewer.locationCode] : LOCATION_ORDER;
  const positionUnknown = position !== '' && !(POSITIONS as readonly string[]).includes(position);
  const clinicUnknown = homeLocationCode !== '' && !(clinicOptions as readonly string[]).includes(homeLocationCode);
  const canSave = firstName.trim() !== '' && lastName.trim() !== '';

  async function save(): Promise<void> {
    if (!canSave) return;
    let succeeded = false;
    await write.run(async () => {
      try {
        await updateCandidate(candidate.id, {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          licenseNumber: licenseNumber.trim(),
          position,
          homeLocationCode,
          hiringManager: hiringManager.trim(),
          address: address.trim(),
          city: city.trim(),
          state: stateAbbr.trim(),
          zip: zip.trim(),
        });
        succeeded = true;
        setConflictId(null);
      } catch (err) {
        const conflict = existingCandidateConflict(err);
        if (conflict) { setConflictId(conflict.existingCandidateId); return; }
        throw err;
      }
    });
    if (succeeded) onDone();
  }

  return (
    <div data-testid="edit-form">
      <div class="money-add">
        <label class="nh-field">
          <span class="nh-label">First name</span>
          <input type="text" value={firstName} placeholder="First name" aria-label="First name" disabled={write.busy} onInput={(e: Event) => setFirstName((e.target as HTMLInputElement).value)} />
        </label>
        <label class="nh-field">
          <span class="nh-label">Last name</span>
          <input type="text" value={lastName} placeholder="Last name" aria-label="Last name" disabled={write.busy} onInput={(e: Event) => setLastName((e.target as HTMLInputElement).value)} />
        </label>
        <label class="nh-field">
          <span class="nh-label">Email</span>
          <input type="email" value={email} placeholder="Email" aria-label="Email" disabled={write.busy} onInput={(e: Event) => setEmail((e.target as HTMLInputElement).value)} />
        </label>
        <label class="nh-field">
          <span class="nh-label">Phone</span>
          <input type="text" value={phone} placeholder="Phone" aria-label="Phone" disabled={write.busy} onInput={(e: Event) => setPhone((e.target as HTMLInputElement).value)} />
        </label>
        <label class="nh-field">
          <span class="nh-label">License number</span>
          <input type="text" value={licenseNumber} placeholder="License number" aria-label="License number" disabled={write.busy} onInput={(e: Event) => setLicenseNumber((e.target as HTMLInputElement).value)} />
        </label>
        <label class="nh-field">
          <span class="nh-label">Position</span>
          <select value={position} aria-label="Position" disabled={write.busy} onChange={(e: Event) => setPosition((e.target as HTMLSelectElement).value)}>
            <option value="">—</option>
            {positionUnknown ? <option value={position} disabled>{position}</option> : null}
            {POSITIONS.map((p) => <option key={p} value={p}>{POSITION_LABEL[p]}</option>)}
          </select>
        </label>
      </div>
      <div class="money-add">
        <label class="nh-field">
          <span class="nh-label">Clinic</span>
          <select value={homeLocationCode} aria-label="Clinic" disabled={write.busy} onChange={(e: Event) => setHomeLocationCode((e.target as HTMLSelectElement).value)}>
            <option value="">—</option>
            {clinicUnknown ? <option value={homeLocationCode} disabled>{homeLocationCode}</option> : null}
            {clinicOptions.map((code) => <option key={code} value={code}>{LOCATION_NAMES[code]}</option>)}
          </select>
        </label>
        <label class="nh-field">
          <span class="nh-label">Hiring manager</span>
          <input type="text" value={hiringManager} placeholder="Hiring manager" aria-label="Hiring manager" disabled={write.busy} onInput={(e: Event) => setHiringManager((e.target as HTMLInputElement).value)} />
        </label>
        <label class="nh-field">
          <span class="nh-label">Address</span>
          <input type="text" value={address} placeholder="Address" aria-label="Address" disabled={write.busy} onInput={(e: Event) => setAddress((e.target as HTMLInputElement).value)} />
        </label>
        <label class="nh-field">
          <span class="nh-label">City</span>
          <input type="text" value={city} placeholder="City" aria-label="City" disabled={write.busy} onInput={(e: Event) => setCity((e.target as HTMLInputElement).value)} />
        </label>
        <label class="nh-field">
          <span class="nh-label">State</span>
          <input type="text" value={stateAbbr} placeholder="State" aria-label="State" disabled={write.busy} onInput={(e: Event) => setStateAbbr((e.target as HTMLInputElement).value)} />
        </label>
        <label class="nh-field">
          <span class="nh-label">Zip</span>
          <input type="text" value={zip} placeholder="Zip" aria-label="Zip" disabled={write.busy} onInput={(e: Event) => setZip((e.target as HTMLInputElement).value)} />
        </label>
      </div>
      <div class="money-add">
        <button type="button" class="btn primary" disabled={write.busy || !canSave} onClick={() => void save()} data-testid="edit-save">
          {write.busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" class="btn" onClick={onCancel} data-testid="edit-cancel">Cancel</button>
      </div>
      {conflictId !== null ? (
        <p class="bad" role="alert">
          Already on file as candidate #<span data-testid="edit-existing-id">{conflictId}</span>
        </p>
      ) : null}
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </div>
  );
}

/**
 * Compose card (docs/RECRUITING-PLAN.md item 5). From is the VIEWER's own
 * address, read-only (ruling 4: "sending is from a box in the app on the
 * manager's own address" — never a field a person could type someone else
 * into); To is the candidate's own address, also read-only (the SERVER
 * re-validates it matches the candidate's email on file regardless — see
 * src/recruiting_email.js — so showing an editable field here would only
 * invite a refusal the person did not expect). Picking a template fills
 * Subject/Body but does not lock them — a manager may still edit before
 * sending. "Check Spelling/Grammar" (owner, 2026-09-28; was "Fix wording") sends ONLY the current draft body to the model
 * (never candidate data) and replaces the body with the corrected text.
 */
function EmailForm(
  { candidate, viewer, onDone, onCancel }:
  { candidate: Candidate; viewer: RecruitingViewer; onDone: () => void; onCancel: () => void },
): JSX.Element {
  const templatesLoad = useResource(RECRUITING_TEMPLATES_KEY, fetchRecruitingTemplates);
  const templates = templatesLoad.status === 'ready' ? templatesLoad.data.templates : [];
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [fixing, setFixing] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);
  const write = useWrite();

  // Direct address first; otherwise the Indeed relay, which forwards to them.
  const to = candidate.email ?? candidate.emailRelay ?? '';
  const canSave = !write.busy && !fixing && to !== '' && subject.trim() !== '' && body.trim() !== '';

  function applyTemplate(idStr: string): void {
    setTemplateId(idStr);
    const picked = templates.find((t) => String(t.id) === idStr);
    if (picked) {
      setSubject(picked.subject);
      setBody(picked.body);
    }
  }

  async function fixWording(): Promise<void> {
    if (fixing || write.busy || body.trim() === '') return;
    setFixing(true);
    setFixError(null);
    try {
      const { text } = await fixRecruitingWording(body);
      setBody(text);
    } catch (err) {
      setFixError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setFixing(false);
    }
  }

  function save(): void {
    if (!canSave) return;
    void write.run(async () => {
      await sendCandidateEmail(candidate.id, {
        to, subject: subject.trim(), body, templateId: templateId ? Number(templateId) : undefined,
      });
    }, onDone);
  }

  return (
    <div data-testid="email-form" class="recruiting-compose">
      {to === '' ? (
        <>
          <p class="bad" role="alert">This person has no email address on file. Add one with Edit first.</p>
          <div class="actions">
            <button type="button" class="btn" onClick={onCancel} data-testid="email-cancel">Close</button>
          </div>
        </>
      ) : (<>
      {!candidate.email && candidate.emailRelay && /@indeedemail\.com$/i.test(candidate.emailRelay) ? (
        <p class="form-error" role="note" data-testid="indeed-relay-warning">
          This is an Indeed relay address. Indeed only delivers it if your email has candidate access to this job in Indeed — ask Tomer to add you if a send bounces.
        </p>
      ) : null}
      <div class="recruiting-compose-head">
        <label class="nh-field">
          <span class="nh-label">From</span>
          <input type="text" aria-label="From" value={viewer.email} readOnly disabled />
        </label>
        <label class="nh-field">
          <span class="nh-label">To</span>
          <input type="text" aria-label="To" value={to} readOnly disabled />
        </label>
        <label class="nh-field">
          <span class="nh-label">Template</span>
          <select
            aria-label="Template" value={templateId} disabled={write.busy || fixing}
            onChange={(e: Event) => applyTemplate((e.target as HTMLSelectElement).value)}
          >
            <option value="">Template…</option>
            {templates.map((t) => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
          </select>
        </label>
      </div>
      <label class="nh-field">
        <span class="nh-label">Subject</span>
        <input
          type="text" aria-label="Subject" value={subject} disabled={write.busy || fixing}
          onInput={(e: Event) => setSubject((e.target as HTMLInputElement).value)}
        />
      </label>
      <label class="nh-field">
        <span class="nh-label">Body</span>
        <textarea
          class="notes-entry" rows={12} aria-label="Body" value={body} disabled={write.busy || fixing}
          spellcheck
          onInput={(e: Event) => setBody((e.target as HTMLTextAreaElement).value)}
        />
      </label>
      <div class="actions">
        <button
          type="button" class="btn" disabled={write.busy || fixing || body.trim() === ''}
          onClick={() => void fixWording()} data-testid="email-fix-wording"
        >
          {fixing ? 'Checking…' : 'Check Spelling/Grammar'}
        </button>
        <button type="button" class="btn primary" disabled={!canSave} onClick={save} data-testid="email-send">
          {write.busy ? 'Sending…' : 'Send'}
        </button>
        <button type="button" class="btn" onClick={onCancel} data-testid="email-cancel">Cancel</button>
      </div>
      {fixError ? <small class="bad" role="alert">{fixError}</small> : null}
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
      </>)}
    </div>
  );
}

function AttachLinkForm(
  { candidateId, onDone, onCancel }:
  { candidateId: number; onDone: () => void; onCancel: () => void },
): JSX.Element {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const write = useWrite();
  const canSave = url.trim() !== '';

  function save(): void {
    if (!canSave) return;
    void write.run(async () => {
      await attachCandidateFile(candidateId, url.trim(), name.trim() || undefined);
    }, onDone);
  }

  return (
    <div data-testid="attach-form">
      <div class="attach-row">
        <input
          type="text" class="attach-link" aria-label="Drive link" placeholder="https://drive.google.com/…"
          value={url} disabled={write.busy} onInput={(e: Event) => setUrl((e.target as HTMLInputElement).value)}
        />
        <input
          type="text" class="attach-name" aria-label="Shown as" placeholder="What to call it"
          value={name} disabled={write.busy} onInput={(e: Event) => setName((e.target as HTMLInputElement).value)}
        />
        <button type="button" class="btn primary" disabled={write.busy || !canSave} onClick={save} data-testid="attach-save">
          {write.busy ? 'Saving…' : 'Add link'}
        </button>
        <button type="button" class="btn" onClick={onCancel} data-testid="attach-cancel">Cancel</button>
      </div>
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </div>
  );
}

/** One side of the merge's side-by-side compare. */
function CompareCard({ label, row }: { label: string; row: Candidate | CandidateRow }): JSX.Element {
  return (
    <div class="candidate-facts">
      <p><b>{label}</b></p>
      <table>
        <tbody>
          <tr><th scope="row">Name</th><td>{row.lastName}, {row.firstName}</td></tr>
          <tr><th scope="row">Email</th><td>{row.email || row.emailRelay || '—'}</td></tr>
          <tr><th scope="row">Phone</th><td>{row.phone || '—'}</td></tr>
          <tr><th scope="row">License</th><td>{row.licenseNumber || '—'}</td></tr>
          <tr><th scope="row">Stage</th><td>{STAGE_LABEL[row.stage] ?? row.stage}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

/**
 * Merge into another record (rule 8 leftover, docs/RECRUITING-PLAN.md item
 * "merge two records that are the same person" — OWNER/DIRECTOR only, the
 * chip itself is not even rendered for anyone else, CandidateActions below).
 * The record whose page is OPEN is always kept as the survivor — a
 * deliberate, simple rule (recorded in the build report) rather than asking
 * which side to keep, since "afterwards the page shows the survivor" only
 * needs to be true of the page already on screen. The search reuses the
 * SAME board search endpoint (fetchRecruitingCandidates), never a fuzzy
 * name match of its own.
 */
function MergeForm(
  { candidate, onDone, onCancel }:
  { candidate: Candidate; onDone: () => void; onCancel: () => void },
): JSX.Element {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<CandidateRow[]>([]);
  const [selected, setSelected] = useState<CandidateRow | null>(null);
  const [reason, setReason] = useState('');
  const write = useWrite();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (debounced.trim().length < 2) { setResults([]); return; }
    let cancelled = false;
    void fetchRecruitingCandidates({ location: 'ALL', q: debounced, limit: 8, offset: 0 }).then((r) => {
      if (!cancelled) setResults(r.rows.filter((row) => row.id !== candidate.id));
    });
    return () => { cancelled = true; };
  }, [debounced, candidate.id]);

  const canSave = selected !== null && reason.trim() !== '';

  function save(): void {
    if (!canSave || !selected) return;
    void write.run(async () => {
      await mergeCandidate(candidate.id, selected.id, reason.trim());
    }, onDone);
  }

  return (
    <div data-testid="merge-form">
      {!selected ? (
        <>
          <label class="nh-field">
            <span class="nh-label">Find the other record</span>
            <input
              type="search" aria-label="Find the other record" placeholder="Name, phone, email, license"
              value={query} onInput={(e: Event) => setQuery((e.target as HTMLInputElement).value)}
            />
          </label>
          {results.length > 0 ? (
            <ul class="queue" data-testid="merge-search-results">
              {results.map((r) => (
                <li key={r.id}>
                  <button type="button" class="btn" data-testid={`merge-pick-${r.id}`} onClick={() => setSelected(r)}>
                    {r.lastName}, {r.firstName} — {r.email || r.emailRelay || r.phone || 'no contact on file'}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : (
        <>
          <div class="candidate-columns">
            <CompareCard label="Keeping (this page)" row={candidate} />
            <CompareCard label={`Merging away — #${selected.id}`} row={selected} />
          </div>
          <label class="nh-field">
            <span class="nh-label">Why these are the same person</span>
            <input
              type="text" aria-label="Reason" value={reason} disabled={write.busy}
              onInput={(e: Event) => setReason((e.target as HTMLInputElement).value)}
            />
          </label>
          <div class="money-add">
            <button type="button" class="btn primary" disabled={write.busy || !canSave} onClick={save} data-testid="merge-save">
              {write.busy ? 'Merging…' : 'Merge — keep this record'}
            </button>
            <button type="button" class="btn" onClick={() => setSelected(null)} data-testid="merge-change">Choose someone else</button>
          </div>
        </>
      )}
      <div class="money-add">
        <button type="button" class="btn" onClick={onCancel} data-testid="merge-cancel">Cancel</button>
      </div>
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </div>
  );
}

const DOH_SEARCH_URL = 'https://mqa-internet.doh.state.fl.us/MQASearchServices/HealthCareProviders';
const LICENSE_RESULTS: ReadonlyArray<{ value: LicenseCheckedStatus; label: string }> = [
  { value: 'VALID', label: 'Clear / active' },
  { value: 'EXPIRED', label: 'Expired or inactive' },
  { value: 'NOT_FOUND', label: 'Not found' },
  { value: 'DISCIPLINED', label: 'Discipline on file' },
];

/** "License checked on DOH" (owner, 2026-09-28): the manager opens the Florida
 * DOH search, looks the person up, and records what they saw here. The app
 * never checks a license itself. */
function SendToManagerForm(
  { candidate, onDone, onCancel }:
  { candidate: Candidate; onDone: () => void; onCancel: () => void },
): JSX.Element {
  const home = candidate.homeLocationCode ?? '';
  const [clinic, setClinic] = useState(home || LOCATION_ORDER[0]);
  const [move, setMove] = useState(false);
  const [putBack, setPutBack] = useState(false);
  const [stage, setStage] = useState('TO_REVIEW');
  const [note, setNote] = useState('');
  const [mailProblem, setMailProblem] = useState<string | null>(null);
  const write = useWrite();
  const canSave = note.trim() !== '';
  const differs = clinic !== home;

  function save(): void {
    if (!canSave) return;
    void write.run(async () => {
      const res = await sendToManager(candidate.id, {
        clinic, note: note.trim(), moveToClinic: differs && move, putBack, stage: putBack ? stage : undefined,
      });
      // Loud: the note is saved, but the manager was not emailed.
      if (!res.mail.sent) {
        setMailProblem(`The note is saved on the candidate, but the manager was NOT emailed: ${res.mail.reason ?? 'unknown reason'}.`);
        return;
      }
      onDone();
    });
  }

  return (
    <div data-testid="send-manager-form">
      <label class="nh-field">
        <span class="nh-label">Clinic</span>
        <select
          aria-label="Clinic" value={clinic} disabled={write.busy}
          onChange={(e: Event) => setClinic((e.target as HTMLSelectElement).value)}
        >
          {LOCATION_ORDER.map((c) => <option key={c} value={c}>{LOCATION_NAMES[c] ?? c}</option>)}
        </select>
      </label>
      {differs ? (
        <label class="nh-field">
          <input
            type="checkbox" checked={move} disabled={write.busy} data-testid="send-manager-move"
            onChange={(e: Event) => setMove((e.target as HTMLInputElement).checked)}
          />
          {' '}Move to this clinic
        </label>
      ) : null}
      <label class="nh-field">
        <input
          type="checkbox" checked={putBack} disabled={write.busy} data-testid="send-manager-putback"
          onChange={(e: Event) => setPutBack((e.target as HTMLInputElement).checked)}
        />
        {' '}Put back on hire list
      </label>
      {putBack ? (
        <label class="nh-field">
          <span class="nh-label">Stage</span>
          <select
            aria-label="Stage" value={stage} disabled={write.busy}
            onChange={(e: Event) => setStage((e.target as HTMLSelectElement).value)}
          >
            {RECRUITING_STAGES.filter((s) => s !== 'HIRED').map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
          </select>
        </label>
      ) : null}
      <label class="nh-field">
        <span class="nh-label">Note to the manager</span>
        <textarea
          class="notes-entry" rows={5} aria-label="Note to the manager"
          value={note} disabled={write.busy}
          onInput={(e: Event) => setNote((e.target as HTMLTextAreaElement).value)}
        />
      </label>
      <div class="money-add">
        <button
          type="button" class="btn primary" disabled={write.busy || !canSave || mailProblem !== null}
          onClick={save} data-testid="send-manager-save"
        >
          {write.busy ? 'Sending…' : 'Send'}
        </button>
        <button type="button" class="btn" onClick={mailProblem ? onDone : onCancel} data-testid="send-manager-cancel">
          {mailProblem ? 'Close' : 'Cancel'}
        </button>
      </div>
      {mailProblem ? <p class="bad" role="alert" data-testid="send-manager-mail-problem">{mailProblem}</p> : null}
      {write.error && !mailProblem ? <small class="bad" role="alert">{write.error}</small> : null}
    </div>
  );
}

function LicenseCheckedForm(
  { candidate, onDone, onCancel }:
  { candidate: Candidate; onDone: () => void; onCancel: () => void },
): JSX.Element {
  const [status, setStatus] = useState<LicenseCheckedStatus>('VALID');
  const [expires, setExpires] = useState('');
  const [note, setNote] = useState('');
  const write = useWrite();

  function save(): void {
    void write.run(async () => {
      await markLicenseChecked(candidate.id, {
        licenseStatus: status,
        licenseExpires: expires || undefined,
        note: note.trim() || undefined,
      });
    }, onDone);
  }

  return (
    <div data-testid="license-checked-form">
      <p class="hint">
        License {candidate.licenseNumber || '(no number on file)'}: look it up on the Florida DOH site, then record what it shows.
      </p>
      <div class="money-add">
        <a class="btn" href={DOH_SEARCH_URL} target="_blank" rel="noreferrer" data-testid="doh-link">Open DOH license search</a>
        <label class="nh-field">
          <span class="nh-label">Result</span>
          <select
            aria-label="Result" value={status} disabled={write.busy}
            onChange={(e: Event) => setStatus((e.target as HTMLSelectElement).value as LicenseCheckedStatus)}
          >
            {LICENSE_RESULTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </label>
        <label class="nh-field">
          <span class="nh-label">Expires</span>
          <input
            type="date" aria-label="Expires" value={expires} disabled={write.busy}
            onInput={(e: Event) => setExpires((e.target as HTMLInputElement).value)}
          />
        </label>
        <label class="nh-field">
          <span class="nh-label">Note</span>
          <input
            type="text" aria-label="Note" value={note} disabled={write.busy}
            onInput={(e: Event) => setNote((e.target as HTMLInputElement).value)}
          />
        </label>
        <button type="button" class="btn primary" disabled={write.busy} onClick={save} data-testid="license-checked-save">
          {write.busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" class="btn" onClick={onCancel} data-testid="license-checked-cancel">Cancel</button>
      </div>
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </div>
  );
}


// ---------------------------------------------------------------------------
// The candidate page's tabs (owner, 2026-09-28): "I'd rather have these chips
// be tabs … first tab should be Profile containing all … some drop downs in
// the profile body for marking things like move stage, Outcome … do not
// contact should be a chip in there or attach link. License check should be a
// tab as should notes and email tabs. Hired should be in stage, not another
// hire chip. Referred by should be part of the fields of Profile" — "and a
// selected tab should be in purple".
// ---------------------------------------------------------------------------

/** Stage, right in the profile: the select shows the current stage; picking
 * another reveals "Why" and Save (a one-line note on every stage change —
 * decision 7). Picking Hired saves as a hire and goes on to the prefilled New
 * hire, which the old Hire chip did. A do-not-contact person can only be
 * moved to Hold. A 409 (an archived outcome) offers "Reopen and move". */
export function StageField(
  { candidate, onChanged }:
  { candidate: Candidate; onChanged: () => void },
): JSX.Element {
  const [stage, setStage] = useState<string>(candidate.stage);
  const [note, setNote] = useState('');
  const [reopen, setReopen] = useState<{ message: string; stage: string; note: string } | null>(null);
  const write = useWrite();
  const locked = candidate.doNotContact;
  const changed = stage !== candidate.stage;
  const canSave = changed && note.trim() !== '';

  function reset(): void { setStage(candidate.stage); setNote(''); setReopen(null); }

  async function attempt(targetStage: string, targetNote: string, isReopen?: boolean): Promise<void> {
    let succeeded = false;
    await write.run(async () => {
      try {
        await moveCandidateStage(candidate.id, targetStage, targetNote, isReopen);
        succeeded = true;
        setReopen(null);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && !isReopen) {
          setReopen({ message: err.message, stage: targetStage, note: targetNote });
          return;
        }
        setReopen(null);
        throw err;
      }
    });
    if (!succeeded) return;
    setNote('');
    onChanged();
    if (targetStage === 'HIRED') window.location.assign(managerEntryUrl(candidate.id));
  }

  return (
    <div class="candidate-status-field compact" data-testid="stage-form">
      <label class="nh-field">
        <span class="nh-label">Stage</span>
        <select
          aria-label="Move to stage" value={stage} disabled={write.busy}
          onChange={(e: Event) => setStage((e.target as HTMLSelectElement).value)}
        >
          {RECRUITING_STAGES.map((s) => (
            <option key={s} value={s} disabled={locked && s !== 'HOLD' && s !== candidate.stage}>{STAGE_LABEL[s]}</option>
          ))}
        </select>
      </label>
      {locked ? <p class="bad" role="alert">This person is marked Do not contact. Only Hold is allowed.</p> : null}
      {changed ? (
        <div class="money-add">
          <label class="nh-field">
            <span class="nh-label">{stage === 'HIRED' ? 'Hire note' : 'Why'}</span>
            <input
              type="text" aria-label="Why" value={note} disabled={write.busy}
              onInput={(e: Event) => setNote((e.target as HTMLInputElement).value)}
            />
          </label>
          <button
            type="button" class="btn primary" disabled={write.busy || !canSave}
            onClick={() => void attempt(stage, note.trim())} data-testid="stage-save"
          >
            {write.busy ? 'Saving…' : stage === 'HIRED' ? 'Hire' : 'Save'}
          </button>
          <button type="button" class="btn" onClick={reset} data-testid="stage-cancel">Cancel</button>
          {reopen ? (
            <button
              type="button" class="btn" disabled={write.busy} data-testid="stage-reopen"
              onClick={() => void attempt(reopen.stage, reopen.note, true)}
            >
              Reopen and move
            </button>
          ) : null}
        </div>
      ) : null}
      {stage === 'HIRED' && changed ? <p class="hint">Saving opens the prefilled New hire on Manager Entry.</p> : null}
      {reopen ? <p class="bad" role="alert">{reopen.message}</p> : null}
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </div>
  );
}

/** Outcome, right in the profile: picking one reveals Reason and Save. */
export function OutcomeField(
  { candidate, onChanged, viewer = null }:
  { candidate: Candidate; onChanged: () => void; viewer?: RecruitingViewer | null },
): JSX.Element {
  const canMarkCurrent = viewer?.role === 'OWNER' || viewer?.role === 'DIRECTOR';
  const current = (OUTCOMES as readonly string[]).includes(candidate.outcome ?? '') ? (candidate.outcome as string) : '';
  const [outcome, setOutcome] = useState(current);
  const [reason, setReason] = useState('');
  const write = useWrite();
  const changed = outcome !== current && outcome !== '';
  const canSave = changed && reason.trim() !== '';

  function save(): void {
    if (!canSave) return;
    void write.run(async () => {
      await setCandidateOutcome(candidate.id, outcome, reason.trim());
    }, () => { setReason(''); onChanged(); });
  }

  return (
    <div class="candidate-status-field compact" data-testid="outcome-form">
      <label class="nh-field">
        <span class="nh-label">Outcome</span>
        <select
          aria-label="Outcome" value={outcome} disabled={write.busy}
          onChange={(e: Event) => setOutcome((e.target as HTMLSelectElement).value)}
        >
          <option value="" disabled={current !== ''}>{current ? 'Reopen through Stage' : 'None — still active'}</option>
          {OUTCOMES.filter((o) => o !== 'CURRENT_EMPLOYEE' || canMarkCurrent || o === current).map((o) => <option key={o} value={o}>{OUTCOME_LABEL[o]}</option>)}
        </select>
      </label>
      {changed ? (
        <div class="money-add">
          <label class="nh-field">
            <span class="nh-label">Reason</span>
            <input
              type="text" aria-label="Reason" value={reason} disabled={write.busy}
              onInput={(e: Event) => setReason((e.target as HTMLInputElement).value)}
            />
          </label>
          <button type="button" class="btn primary" disabled={write.busy || !canSave} onClick={save} data-testid="outcome-save">
            {write.busy ? 'Saving…' : 'Save'}
          </button>
          <button type="button" class="btn" onClick={() => { setOutcome(current); setReason(''); }} data-testid="outcome-cancel">Cancel</button>
        </div>
      ) : null}
      {outcome === 'NOT_ELIGIBLE' && changed ? <p class="hint">This also marks the person Do not contact.</p> : null}
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </div>
  );
}

/** Referred by, right in the profile — an exact, active roster row (rule 8). */
export function ReferredByField(
  { candidate, onChanged }:
  { candidate: Candidate; onChanged: () => void },
): JSX.Element {
  const roster = useResource<{ people: RosterPerson[] }>('people-roster', async () => ({ people: [] }));
  const people = (roster.status === 'ready' ? roster.data.people : []).filter((p) => p.status === 'ACTIVE');
  const current = candidate.referredByEmployeeId != null ? String(candidate.referredByEmployeeId) : '';
  const [employeeId, setEmployeeId] = useState(current);
  const write = useWrite();
  const changed = employeeId !== current;

  function save(): void {
    void write.run(async () => {
      await setCandidateReferral(candidate.id, employeeId ? Number(employeeId) : null);
    }, onChanged);
  }

  return (
    <div class="candidate-status-field compact inline-cell" data-testid="referral-form">
      <label class="nh-field">
        <select
          aria-label="Referred by" value={employeeId} disabled={write.busy}
          onChange={(e: Event) => setEmployeeId((e.target as HTMLSelectElement).value)}
        >
          <option value="">— none —</option>
          {current && !people.some((p) => String(p.id) === current) ? (
            <option value={current}>{candidate.referredByName || `#${current}`}</option>
          ) : null}
          {people.map((p) => <option key={p.id} value={String(p.id)}>{p.full_name}</option>)}
        </select>
      </label>
      {changed ? (
        <div class="money-add">
          <button type="button" class="btn primary" disabled={write.busy} onClick={save} data-testid="referral-save">
            {write.busy ? 'Saving…' : 'Save'}
          </button>
          <button type="button" class="btn" onClick={() => setEmployeeId(current)} data-testid="referral-cancel">Cancel</button>
        </div>
      ) : null}
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </div>
  );
}

type TabKey = 'profile' | 'notes' | 'email' | 'license';
type ProfileAction = 'edit' | 'dnc' | 'attach' | 'merge' | 'send';
type NoteKind = 'NOTE' | 'CALL' | 'TEXT';

/** The candidate page below the name: the tab bar, and each tab's body. The
 * read-only parts (facts, applications, files, resume; the log) come in from
 * CandidateCard.tsx as `profileBody` / `logBody` / `emailLogBody`, so this
 * file keeps every write and CandidateCard keeps every read. With no viewer
 * (read-only), the tabs still show; no control does. */
export function CandidateActions(
  {
    candidate, viewer, onChanged, profileBody = null, logBody = null, emailLogBody = null, licenseLine = null,
  }: {
    candidate: Candidate;
    viewer: RecruitingViewer | null;
    onChanged: () => void;
    profileBody?: JSX.Element | null;
    logBody?: JSX.Element | null;
    emailLogBody?: JSX.Element | null;
    licenseLine?: JSX.Element | null;
  },
): JSX.Element {
  const [tab, setTab] = useState<TabKey>('profile');
  const [action, setAction] = useState<ProfileAction | null>(null);
  const [noteKind, setNoteKind] = useState<NoteKind>('NOTE');
  const [formKey, setFormKey] = useState(0);
  const toggle = (key: ProfileAction): void => setAction((cur) => (cur === key ? null : key));
  const closeAction = (): void => setAction(null);
  const doneAction = (): void => { setAction(null); onChanged(); };

  const showDncChip = !candidate.doNotContact || viewer?.role === 'OWNER';
  const dncLabel = candidate.doNotContact ? 'Lift do not contact' : 'Do not contact';
  const canMerge = viewer?.role === 'OWNER' || viewer?.role === 'DIRECTOR';
  const noAddress = !candidate.email && !candidate.emailRelay;
  const emailTitle = candidate.doNotContact
    ? `Do not contact — ${candidate.dncReason || 'walled'}`
    : (noAddress ? 'No email address on file' : undefined);

  const tabs: { key: TabKey; label: string; disabled?: boolean; title?: string }[] = [
    { key: 'profile', label: 'Profile' },
    { key: 'notes', label: 'Notes' },
    { key: 'email', label: 'Email', disabled: candidate.doNotContact, title: emailTitle },
    { key: 'license', label: 'License check' },
  ];

  return (
    <div data-testid="candidate-actions">
      {/* Owner, 2026-09-28: Edit, Attach link, Merge and Do not contact sit to
          the right of the tabs, on the same line, as chips. */}
      <div class="candidate-tab-row">
        <div class="clinic-tabs candidate-tabs" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.key} type="button" role="tab" aria-selected={tab === t.key}
              class={`tab${tab === t.key ? ' active' : ''}`}
              disabled={t.disabled} title={t.title}
              onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {viewer ? (
          <div class="actions candidate-tab-chips">
            <button type="button" class="btn" aria-pressed={action === 'edit'} onClick={() => toggle('edit')} data-testid="chip-edit">Edit</button>
            <button type="button" class="btn" aria-pressed={action === 'attach'} onClick={() => toggle('attach')} data-testid="chip-attach">Attach link</button>
            {canMerge ? (
              <button type="button" class="btn" aria-pressed={action === 'merge'} onClick={() => toggle('merge')} data-testid="chip-merge">Merge</button>
            ) : null}
            {canMerge ? (
              <button type="button" class="btn" aria-pressed={action === 'send'} onClick={() => toggle('send')} data-testid="chip-send-manager">Send to manager</button>
            ) : null}
            {showDncChip ? (
              <button type="button" class="btn" aria-pressed={action === 'dnc'} onClick={() => toggle('dnc')} data-testid="chip-dnc">{dncLabel}</button>
            ) : null}
          </div>
        ) : null}
      </div>
      {viewer && action ? (
        <div class="recruiting-action-panel" data-testid="recruiting-action-panel">
          {action === 'edit' ? <EditForm candidate={candidate} viewer={viewer} onDone={doneAction} onCancel={closeAction} /> : null}
          {action === 'dnc' ? <DoNotContactForm candidate={candidate} onDone={doneAction} onCancel={closeAction} /> : null}
          {action === 'attach' ? <AttachLinkForm candidateId={candidate.id} onDone={doneAction} onCancel={closeAction} /> : null}
          {action === 'send' ? <SendToManagerForm candidate={candidate} onDone={doneAction} onCancel={closeAction} /> : null}
          {action === 'merge' ? <MergeForm candidate={candidate} onDone={doneAction} onCancel={closeAction} /> : null}
        </div>
      ) : null}

      {tab === 'profile' ? (
        <div data-testid="candidate-tab-profile">
          {profileBody}
        </div>
      ) : null}

      {tab === 'notes' ? (
        <div data-testid="candidate-tab-notes">
          {viewer ? (
            <>
              <div class="actions">
                {(['NOTE', 'CALL', 'TEXT'] as NoteKind[]).map((k) => (
                  <button
                    key={k} type="button" class="btn recruiting-chip" aria-pressed={noteKind === k}
                    onClick={() => setNoteKind(k)} data-testid={`chip-${k.toLowerCase()}`}
                  >
                    {k === 'NOTE' ? 'Note' : k === 'CALL' ? 'Call' : 'Text'}
                  </button>
                ))}
              </div>
              <div class="recruiting-action-panel">
                <LogForm
                  key={`${noteKind}-${formKey}`} kind={noteKind} candidateId={candidate.id}
                  onDone={() => { setFormKey((n) => n + 1); onChanged(); }}
                  onCancel={() => setFormKey((n) => n + 1)}
                />
              </div>
            </>
          ) : null}
          {logBody}
        </div>
      ) : null}

      {tab === 'email' ? (
        <div data-testid="candidate-tab-email">
          {viewer && !candidate.doNotContact ? (
            <div class="recruiting-action-panel">
              <EmailForm
                key={`email-${formKey}`} candidate={candidate} viewer={viewer}
                onDone={() => { setFormKey((n) => n + 1); onChanged(); }}
                onCancel={() => setFormKey((n) => n + 1)}
              />
            </div>
          ) : null}
          {emailLogBody}
        </div>
      ) : null}

      {tab === 'license' ? (
        <div data-testid="candidate-tab-license">
          {licenseLine}
          {viewer ? (
            <div class="recruiting-action-panel">
              <LicenseCheckedForm
                key={`license-${formKey}`} candidate={candidate}
                onDone={() => { setFormKey((n) => n + 1); onChanged(); }}
                onCancel={() => setFormKey((n) => n + 1)}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
